import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';
import { ArrowRight, Check, X, Clock, Users, Brain, Leaf, Wrench, Crown, Sun } from 'lucide-react';

// Image URLs
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/pj01nsvk_IMG_0346.JPG",
  classroom: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/rqxgj890_IMG_0358.JPG",
  reading: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/u572a801_IMG_0347.PNG",
  agriculture1: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/jnksp1ed_IMG_0354.JPG",
  agriculture2: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/hwyxoe1c_IMG_0366.JPG",
  technology: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/2jyoq9a0_IMG_0361.jpg",
};

const domains = [
  { icon: Brain, name: 'Technology', desc: 'Build, code, create' },
  { icon: Wrench, name: 'Trade', desc: 'Make, fix, sell' },
  { icon: Leaf, name: 'Agriculture', desc: 'Grow, nurture, sustain' },
  { icon: Crown, name: 'Leadership', desc: 'Lead, decide, serve' },
];

const dailySchedule = [
  { time: '8:00', activity: 'Arrival & Free Build/Play' },
  { time: '9:00', activity: 'Personalised Academic Mastery' },
  { time: '11:00', activity: 'Outdoor Break' },
  { time: '12:00', activity: 'Applied Learning (Tech / Trade / Agriculture)' },
  { time: '1:30', activity: 'Lunch' },
  { time: '2:00', activity: 'Team Challenges / Sport / Storytelling' },
  { time: '3:00', activity: 'Pick Up' },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="page-transition">
      {/* HERO SECTION */}
      <section data-testid="hero-section" className="relative h-screen min-h-[750px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 animate-slow-zoom origin-center">
            <img
              src={IMAGES.hero}
              alt="Children at Nova Roots"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40" />
        </div>
        
        <div className="relative z-10 container-nova text-white pt-24">
          <ScrollReveal>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-[1.15] max-w-4xl">
              Education is not preparing children<br className="hidden md:block" /> for the world ahead.
              <span className="block mt-2 text-[#C27856]">Nova Roots is.</span>
            </h1>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <p className="mt-8 text-lg md:text-xl font-light text-white/90 max-w-lg">
              A mastery-based learning village for future-ready children.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={400}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link 
                to="/summer-camp" 
                className="bg-[#C27856] text-white hover:bg-[#A8654A] px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300 text-center shadow-lg"
                data-testid="hero-cta-camp"
              >
                Apply for Summer Camp
              </Link>
              <Link 
                to="/enquire" 
                className="bg-white text-[#1A3C34] hover:bg-[#F9F8F4] px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300 text-center shadow-lg"
                data-testid="hero-cta-cohort"
              >
                Join Founding Cohort
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* HOW NOVA ROOTS WORKS - 3 POINTS */}
      <section className="py-16 md:py-20 bg-[#1A3C34]">
        <div className="container-nova">
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 text-center">
            <ScrollReveal>
              <div className="text-[#F9F8F4]">
                <p className="text-4xl md:text-5xl font-heading font-light text-[#C27856]">2</p>
                <p className="mt-2 text-lg font-medium">hours daily academic mastery</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <div className="text-[#F9F8F4]">
                <p className="text-4xl md:text-5xl font-heading font-light text-[#C27856]">4</p>
                <p className="mt-2 text-lg font-medium">domains of real-world learning</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="text-[#F9F8F4]">
                <p className="text-4xl md:text-5xl font-heading font-light text-[#C27856]">32</p>
                <p className="mt-2 text-lg font-medium">learners, deeply personalised</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* THE 2+4 MODEL - CORE DIFFERENTIATOR */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-3">The Nova Roots Model</p>
              <h2 className="font-heading text-6xl md:text-7xl lg:text-8xl font-light text-[#1A3C34]">2 + 4</h2>
            </div>
          </ScrollReveal>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* 2 Hours */}
            <ScrollReveal>
              <div className="bg-white border border-[#EBE8E0] rounded-2xl p-8 md:p-10 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#1A3C34] flex items-center justify-center">
                    <span className="text-2xl font-heading font-light text-white">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-medium text-[#1A3C34]">Hours</h3>
                    <p className="text-sm text-[#5C6B6B]">Morning Academic Mastery</p>
                  </div>
                </div>
                <p className="text-base font-light text-[#2D3A3A] leading-relaxed">
                  Master academics at your own pace. Literacy, numeracy, science. 
                  No rote learning. No ranking. Real progress, tracked daily.
                </p>
              </div>
            </ScrollReveal>

            {/* 4 Domains */}
            <ScrollReveal delay={150}>
              <div className="bg-[#1A3C34] rounded-2xl p-8 md:p-10 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#C27856] flex items-center justify-center">
                    <span className="text-2xl font-heading font-light text-white">4</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-medium text-[#F9F8F4]">Domains</h3>
                    <p className="text-sm text-[#F9F8F4]/70">Learn How The World Works</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  {domains.map((domain, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <domain.icon className="w-5 h-5 text-[#C27856]" />
                      <div>
                        <p className="text-sm font-medium text-[#F9F8F4]">{domain.name}</p>
                        <p className="text-xs text-[#F9F8F4]/60">{domain.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={300}>
            <div className="mt-12 text-center">
              <Link 
                to="/model" 
                className="inline-flex items-center gap-2 text-[#1A3C34] font-medium hover:text-[#C27856] transition-colors"
              >
                See the full structure
                <ArrowRight size={18} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* A DAY AT NOVA ROOTS */}
      <section className="py-20 md:py-28 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <ScrollReveal>
                <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-3">Daily Rhythm</p>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                  A Day at Nova Roots
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <div className="mt-10 space-y-0">
                  {dailySchedule.map((item, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center gap-6 py-4 ${index < dailySchedule.length - 1 ? 'border-b border-[#1A3C34]/10' : ''}`}
                    >
                      <span className="w-14 text-sm font-medium text-[#C27856]">{item.time}</span>
                      <span className="text-base font-light text-[#2D3A3A]">{item.activity}</span>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal delay={200}>
              <div className="grid grid-cols-2 gap-4">
                <div className="img-hover-zoom rounded-xl overflow-hidden">
                  <img src={IMAGES.reading} alt="Academic mastery" className="w-full h-48 md:h-56 object-cover" />
                </div>
                <div className="img-hover-zoom rounded-xl overflow-hidden mt-8">
                  <img src={IMAGES.technology} alt="Technology learning" className="w-full h-48 md:h-56 object-cover" />
                </div>
                <div className="img-hover-zoom rounded-xl overflow-hidden">
                  <img src={IMAGES.agriculture1} alt="Agriculture" className="w-full h-48 md:h-56 object-cover" />
                </div>
                <div className="img-hover-zoom rounded-xl overflow-hidden mt-8">
                  <img src={IMAGES.classroom} alt="Classroom" className="w-full h-48 md:h-56 object-cover" />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SUMMER CAMP CTA */}
      <section className="py-20 md:py-28 bg-[#C27856]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal>
                <p className="text-sm font-medium uppercase tracking-wider text-white/80 mb-3">July 6th – July 31st</p>
                <h2 className="font-heading text-3xl md:text-4xl font-light text-white">
                  Summer Camp 2026
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <p className="mt-6 text-base font-light text-white/90 max-w-md">
                  A hands-on preview of Nova Roots. Building, creating, exploring, caring for animals. 
                  Limited to 32 children.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={250}>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link 
                    to="/summer-camp" 
                    className="bg-white text-[#1A3C34] hover:bg-[#F9F8F4] px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
                  >
                    Apply Now
                  </Link>
                  <Link 
                    to="/summer-camp#camp-details" 
                    className="border-2 border-white text-white hover:bg-white hover:text-[#C27856] px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
                  >
                    View Details
                  </Link>
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal delay={200}>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-white">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-2xl font-heading">KES 3K</p>
                    <p className="text-sm text-white/70">Daily</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading">KES 13.5K</p>
                    <p className="text-sm text-white/70">Weekly</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading">KES 50K</p>
                    <p className="text-sm text-white/70">Full Camp</p>
                  </div>
                </div>
                <p className="mt-6 text-center text-sm text-white/80">
                  Horse riding available as optional add-on
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FOUNDING COHORT */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-3">September 2026</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                The Founding Cohort
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={150}>
              <p className="mt-6 text-base font-light text-[#5C6B6B] max-w-lg mx-auto">
                12–16 founding learners will shape the first chapter of Nova Roots. 
                Early families help define the model.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={250}>
              <div className="mt-10 grid grid-cols-3 gap-6">
                <div className="bg-white border border-[#EBE8E0] rounded-xl p-6">
                  <p className="text-3xl font-heading text-[#1A3C34]">12–16</p>
                  <p className="text-sm text-[#5C6B6B] mt-1">Founding Learners</p>
                </div>
                <div className="bg-white border border-[#EBE8E0] rounded-xl p-6">
                  <p className="text-3xl font-heading text-[#1A3C34]">4–10</p>
                  <p className="text-sm text-[#5C6B6B] mt-1">Years Old</p>
                </div>
                <div className="bg-white border border-[#EBE8E0] rounded-xl p-6">
                  <p className="text-3xl font-heading text-[#1A3C34]">Runda</p>
                  <p className="text-sm text-[#5C6B6B] mt-1">Nairobi, Kenya</p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={350}>
              <Link 
                to="/enquire" 
                className="mt-10 inline-block bg-[#1A3C34] text-white hover:bg-[#2D4A3E] px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              >
                Join the Founding Cohort
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 md:py-28 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                Is Nova Roots Right For You?
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* FOR */}
            <ScrollReveal>
              <div className="bg-white border border-[#EBE8E0] rounded-2xl p-8">
                <h3 className="font-heading text-lg font-medium text-[#1A3C34] mb-6">Nova Roots is for families who:</h3>
                <ul className="space-y-4">
                  {[
                    'Value independence over obedience',
                    'Want their child deeply seen and challenged',
                    'Are open to non-traditional education',
                    'Believe learning happens everywhere',
                    'Want mastery, not memorisation'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-light text-[#2D3A3A]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            {/* NOT FOR */}
            <ScrollReveal delay={150}>
              <div className="bg-[#1A3C34] rounded-2xl p-8">
                <h3 className="font-heading text-lg font-medium text-[#F9F8F4] mb-6">This is not for families who:</h3>
                <ul className="space-y-4">
                  {[
                    'Want exam-heavy, traditional schooling',
                    'Expect rigid structure and ranking',
                    'Are uncomfortable with innovation',
                    'Prioritise credentials over capability',
                    'Need guaranteed traditional outcomes'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <X className="w-5 h-5 text-[#C27856] mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-light text-[#F9F8F4]/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WALEZI SECTION */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <ScrollReveal>
              <div className="img-hover-zoom rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.classroom}
                  alt="Walezi guiding learners"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </ScrollReveal>

            <div>
              <ScrollReveal>
                <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-3">Our Educators</p>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                  Walezi
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <p className="mt-6 text-base font-light text-[#2D3A3A] leading-relaxed">
                  Walezi means "nurturers" in Swahili. Not teachers who lecture—guides who mentor.
                </p>
                <p className="mt-4 text-base font-light text-[#5C6B6B] leading-relaxed">
                  They observe, challenge and support. Every child is known deeply. 
                  Progress is personal, not comparative.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={250}>
                <div className="mt-8 flex flex-wrap gap-3">
                  {['Guides', 'Mentors', 'Observers', 'Challengers'].map((role, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#F2F0E9] rounded-full text-sm font-light text-[#1A3C34]"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ENVIRONMENT / CAMPUS */}
      <section className="py-20 md:py-28 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-12">
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-3">The Campus</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                A Learning Village
              </h2>
              <p className="mt-4 text-base font-light text-[#5C6B6B] max-w-lg mx-auto">
                Nature, animals, workshops, gardens. Learning happens everywhere.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="img-hover-zoom rounded-xl overflow-hidden">
                <img src={IMAGES.agriculture1} alt="Gardens" className="w-full h-40 md:h-52 object-cover" />
              </div>
              <div className="img-hover-zoom rounded-xl overflow-hidden">
                <img src={IMAGES.agriculture2} alt="Farm" className="w-full h-40 md:h-52 object-cover" />
              </div>
              <div className="img-hover-zoom rounded-xl overflow-hidden">
                <img src={IMAGES.technology} alt="Workshop" className="w-full h-40 md:h-52 object-cover" />
              </div>
              <div className="img-hover-zoom rounded-xl overflow-hidden">
                <img src={IMAGES.reading} alt="Learning spaces" className="w-full h-40 md:h-52 object-cover" />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FOUNDER / CREDIBILITY */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-3">The Founder</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                Built From Belief
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={150}>
              <p className="mt-8 text-base font-light text-[#2D3A3A] leading-relaxed">
                Nova Roots was founded to prove that education can be different. 
                Not as a manifesto—as a working model. Summer Camp 2026 is the first proof point.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={250}>
              <Link 
                to="/founder" 
                className="mt-8 inline-flex items-center gap-2 text-[#1A3C34] font-medium hover:text-[#C27856] transition-colors"
              >
                Read the full story
                <ArrowRight size={18} />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-[#F9F8F4] max-w-2xl mx-auto">
              Ready to see education done differently?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <p className="mt-6 text-base font-light text-[#F9F8F4]/80 max-w-md mx-auto">
              Start with Summer Camp. Or enquire about the Founding Cohort.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/summer-camp" 
                className="bg-[#C27856] text-white hover:bg-[#A8654A] px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              >
                Apply for Summer Camp
              </Link>
              <Link 
                to="/enquire" 
                className="bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              >
                Join Founding Cohort
              </Link>
              <Link 
                to="/enquire" 
                className="border-2 border-[#F9F8F4] text-[#F9F8F4] hover:bg-[#F9F8F4] hover:text-[#1A3C34] px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              >
                Book a Visit
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
